import React, { useState } from 'react';
import {
  ArrowLeft,
  AudioLines,
  BookOpen,
  CalendarDays,
  CreditCard,
  Download,
  Globe,
  HelpCircle,
  Plug,
  ScrollText,
  Settings,
} from 'lucide-react';
import { WindowControls } from '../../components/WindowControls';
import type { ToastKind } from '../../components/Toast';
import { Separator } from '../../components/ui/Input';
import { useLanguage } from '../../lib/i18n';
import { IS_WEB } from '../../lib/platform';
import { useIsNarrow } from '../../lib/useIsNarrow';
import { GeneralSection } from './GeneralSection';
import { LanguageSection } from './LanguageSection';
import { NamesTermsSection } from './NamesTermsSection';
import { RecordingsSection } from './RecordingsSection';
import { CalendarSection } from './CalendarSection';
import { HelpSection } from './HelpSection';
import { ImportSection } from './ImportSection';
import { IntegrationsPanel } from '../integrations/IntegrationsPanel';
import { SubscriptionSection } from './SubscriptionSection';
import { SummaryStyleSection } from './SummaryStyleSection';
import { MobileBackBar, MobileCard, MobileRow } from '../mobile/MobileUI';

/**
 * Kullanıcı ayarları — profil menüsündeki "Ayarlar"dan açılır.
 *
 * Tam sayfa: ana kenar çubuğunun yerini kendi bölüm listesi alıyor, en üstte
 * "Uygulamaya dön".
 */

export type SettingsSectionId =
  | 'general'
  | 'subscription'
  | 'summary-style'
  | 'language'
  | 'names-terms'
  | 'import'
  | 'calendar'
  | 'integrations'
  | 'recordings'
  | 'help';

interface SettingsSection {
  id: SettingsSectionId;
  label: string;
  icon: typeof Settings;
  description: string;
}

/** Etiketler seçili dilde üretildiği için liste her çizimde yeniden kuruluyor. */
function settingsSections(t: (tr: string, en: string) => string): SettingsSection[] {
  const sections = allSettingsSections(t);
  // Web: Granola içe aktarımı tarayıcıdan çağrılamıyor (Granola API'si CORS'a kapalı).
  return IS_WEB ? sections.filter((section) => section.id !== 'import') : sections;
}

function allSettingsSections(t: (tr: string, en: string) => string): SettingsSection[] {
  return [
    { id: 'general', label: t('Genel', 'General'), icon: Settings, description: t('Profilin, görünüm ve davranış.', 'Your profile, appearance and behavior.') },
    { id: 'subscription', label: t('Abonelik', 'Subscription'), icon: CreditCard, description: t('Planın, ödemelerin ve faturaların.', 'Your plan, payments and invoices.') },
    { id: 'summary-style', label: t('Özet stili', 'Summary style'), icon: ScrollText, description: t('Toplantı özetlerinin biçimi ve uzunluğu.', 'Format and length of meeting summaries.') },
    { id: 'language', label: t('Dil', 'Language'), icon: Globe, description: t('Uygulamanın dili ve özetlerin dili.', 'The language of the app and of your summaries.') },
    { id: 'names-terms', label: t('İsimler ve terimler', 'Names & terms'), icon: BookOpen, description: t('Toplantılarında geçen kişiler, şirketler, ürünler ve kısaltmalar — transkripti bunları doğru yazması için yönlendiriyoruz. Bir sonraki kayıttan itibaren geçerli.', 'The people, companies, products and acronyms your meetings mention — we nudge the transcriber to spell them right. Applies from your next recording on.') },
    { id: 'import', label: t('İçe aktar', 'Import'), icon: Download, description: t('Mevcut toplantı notlarını TalkNote’a getir — başlıklar, özetler ve transkriptler, özgün tarihleriyle.', 'Bring your existing meeting notes into TalkNote — titles, summaries, and transcripts, with their original dates.') },
    { id: 'calendar', label: t('Takvim', 'Calendar'), icon: CalendarDays, description: t('Bağlı takvimler ve toplantı tercihleri.', 'Connected calendars and meeting preferences.') },
    { id: 'integrations', label: t('Entegrasyonlar', 'Integrations'), icon: Plug, description: t('Hesabınla bağladığın uygulamalar.', 'The apps you connect with your account.') },
    { id: 'recordings', label: t('Kayıtlar', 'Recordings'), icon: AudioLines, description: t('Kayıt, saklama ve ses tercihleri.', 'Recording, storage and audio preferences.') },
    { id: 'help', label: t('Yardım al', 'Get help'), icon: HelpCircle, description: t('TalkNote ekibine ulaş ya da bir sonraki özelliği bize söyle.', 'Reach the TalkNote team, or tell us what to build next.') },
  ];
}

interface SettingsPageProps {
  isDarkMode: boolean;
  onBack: () => void;
  initialSection?: SettingsSectionId;
  /** Telefonda geri düğmesinin etiketi: ayarların açıldığı ekran ("Toplantılar", "Daha fazla"). */
  backLabel?: string;
  /** Profil kaydedilince oturumdaki kullanıcı (ad, fotoğraf) tazelensin. */
  onProfileSaved: () => void;
  onNotify: (kind: ToastKind, text: string) => void;
  /** Genel > Hesap'taki "Çıkış yap". */
  onSignOut: () => void;
}

export const SettingsPage: React.FC<SettingsPageProps> = ({
  isDarkMode,
  onBack,
  initialSection,
  backLabel,
  onProfileSaved,
  onNotify,
  onSignOut,
}) => {
  const { t } = useLanguage();
  const [activeId, setActiveId] = useState<SettingsSectionId>(initialSection ?? 'general');
  const sections = settingsSections(t);
  const active = sections.find((s) => s.id === activeId) ?? sections[0];
  /**
   * Dar ekranda (telefon) liste ile bölüm yan yana sığmıyor: önce liste,
   * dokununca bölüm, "Ayarlar" ile listeye dönüş. Belirli bir bölümle
   * açıldıysa (ör. bot düğmesinden Abonelik) doğrudan o bölüm.
   */
  const isNarrow = useIsNarrow();
  const [narrowPane, setNarrowPane] = useState<'list' | 'section'>(initialSection ? 'section' : 'list');

  const sectionBody =
    active.id === 'general' ? (
      <GeneralSection onProfileSaved={onProfileSaved} onNotify={onNotify} onSignOut={onSignOut} />
    ) : active.id === 'subscription' ? (
      <SubscriptionSection onNotify={onNotify} />
    ) : active.id === 'summary-style' ? (
      <SummaryStyleSection onNotify={onNotify} />
    ) : active.id === 'language' ? (
      <LanguageSection onNotify={onNotify} />
    ) : active.id === 'names-terms' ? (
      <NamesTermsSection onNotify={onNotify} />
    ) : active.id === 'integrations' ? (
      <IntegrationsPanel onNotify={onNotify} />
    ) : active.id === 'recordings' ? (
      <RecordingsSection onNotify={onNotify} />
    ) : active.id === 'calendar' ? (
      <CalendarSection onNotify={onNotify} />
    ) : active.id === 'import' ? (
      <ImportSection onNotify={onNotify} />
    ) : (
      <HelpSection onNotify={onNotify} />
    );

  /**
   * Telefon: iOS/Android ayarları gibi önce liste, dokununca bölüm. Belirli bir
   * bölümle açıldıysa (profil fotoğrafı → Genel) geri düğmesi doğrudan
   * açıldığı ekrana döner.
   */
  if (isNarrow) {
    const leaveLabel = backLabel ?? t('Geri', 'Back');
    if (narrowPane === 'list') {
      return (
        <div className="flex-1 flex flex-col overflow-hidden bg-background">
          <MobileBackBar label={leaveLabel} onBack={onBack} />
          <main className="flex-1 overflow-y-auto px-5 pb-10 space-y-4">
            <h1 className="pt-1 text-[30px] leading-9 font-bold tracking-tight text-foreground">{t('Ayarlar', 'Settings')}</h1>
            <MobileCard>
              {sections.map((section) => {
                const Icon = section.icon;
                return (
                  <MobileRow
                    key={section.id}
                    icon={<Icon />}
                    title={section.label}
                    onClick={() => {
                      setActiveId(section.id);
                      setNarrowPane('section');
                    }}
                  />
                );
              })}
            </MobileCard>
          </main>
        </div>
      );
    }
    return (
      <div className="flex-1 flex flex-col overflow-hidden bg-background">
        <MobileBackBar
          label={initialSection ? leaveLabel : t('Ayarlar', 'Settings')}
          onBack={() => (initialSection ? onBack() : setNarrowPane('list'))}
        />
        <main className="flex-1 overflow-y-auto select-text">
          <div className="px-5 pt-1 pb-12">
            <h1 className="text-[30px] leading-9 font-bold tracking-tight text-foreground">
              {active.id === 'general' ? t('Genel ayarlar', 'General settings') : active.label}
            </h1>
            <p className="mt-1 mb-5 text-[14px] text-muted-foreground">{active.description}</p>
            {sectionBody}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex-1 flex overflow-hidden">
      {/* Bölüm listesi */}
      <aside
        className={`w-64 h-dvh shrink-0 flex flex-col border-r select-none ${
          isDarkMode ? 'bg-[#0C0C0E] border-[#1E1E22] text-[#D8D8E0]' : 'bg-[#F5F6F8] border-[#E8E8EE] text-[#1E2024]'
        }`}
      >
        <div className="app-drag-region h-12 shrink-0 flex items-center px-3">
          <span className={`text-sm font-bold tracking-[0.18em] ${isDarkMode ? 'text-[#F0EEE6]' : 'text-neutral-900'}`}>
            TALKNOTE
          </span>
        </div>
        <div className="px-3 pb-4 space-y-3 overflow-y-auto no-scrollbar">
          <button
            onClick={onBack}
            className={`app-no-drag flex items-center gap-2 px-3 py-1.5 text-[13.5px] transition-colors ${
              isDarkMode ? 'text-neutral-400 hover:text-[#F0EEE6]' : 'text-neutral-600 hover:text-neutral-900'
            }`}
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{t('Uygulamaya dön', 'Back to app')}</span>
          </button>

          <nav className="space-y-0.5 text-[14px] font-medium">
            {sections.map((section) => {
              const Icon = section.icon;
              const isActive = section.id === activeId;
              return (
                <button
                  key={section.id}
                  id={`settings-nav-${section.id}`}
                  onClick={() => setActiveId(section.id)}
                  aria-current={isActive ? 'page' : undefined}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-left ${
                    isActive
                      ? isDarkMode
                        ? 'bg-[#1F1F23] text-[#F0EEE6] font-semibold'
                        : 'bg-[#E5E7EB] text-neutral-900 font-semibold'
                      : isDarkMode
                        ? 'text-neutral-300 hover:bg-[#19191C] hover:text-[#F0EEE6]'
                        : 'text-neutral-700 hover:bg-neutral-200/70 hover:text-neutral-900'
                  }`}
                >
                  <Icon className={`w-[18px] h-[18px] shrink-0 ${isActive ? 'text-[#46CBE2]' : 'text-neutral-400'}`} />
                  <span>{section.label}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </aside>

      {/* İçerik */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="app-drag-region h-12 shrink-0 flex items-center justify-end px-3.5">
          <div className="app-no-drag">
            <WindowControls isDarkMode={isDarkMode} />
          </div>
        </header>

        <main className="flex-1 overflow-y-auto select-text">
          {/* Abonelikteki dört plan kartı yan yana sığsın diye o bölüm daha geniş. */}
          <div
            className={`mx-auto w-full px-4 md:px-8 pt-6 pb-16 ${
              active.id === 'subscription' ? 'max-w-6xl' : active.id === 'integrations' ? 'max-w-5xl' : 'max-w-3xl'
            }`}
          >
            <div className="space-y-1">
              <h1 className="text-2xl font-bold tracking-tight text-foreground">{active.label}</h1>
              <p className="text-sm text-muted-foreground">{active.description}</p>
            </div>
            <Separator className="my-6" />

            {sectionBody}
          </div>
        </main>
      </div>
    </div>
  );
};
